import { useNavigate } from 'react-router-dom';
import GoogleIcon from '@app/assets/icons/Google.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import DiamondIcon from '@app/assets/icons/diamond.svg?react';
import TrialPlanIcon from '@app/assets/icons/trial-plan.svg?react';
import BriefcaseIcon from '@app/assets/icons/briefcase.svg?react';
import EnterpriseIcon from '@app/assets/icons/enterprise.svg?react';
import { Tooltip } from '../Tooltip/Tooltip';
import { getMembershipInfo } from '@app/utils/utils';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { IntegrationType } from '@app/utils/consts';
import { BusinessPhotoInterface } from '@app/models/business';

interface BusinessSelectProps {
  id: string;
  name: string;
  role?: string;
  google?: string;
  facebook?: string;
  trustpilot?: string;
  membership?: string;
  color?: string;
  integrations?: Array<{ type: string; connectionId?: string }>;
  photo?: BusinessPhotoInterface | null;
}

// Map API plan types to getMembershipInfo expected format
const mapPlanType = (apiPlanType: string): string => {
  switch (apiPlanType) {
    case 'GROWTH':
      return 'Growth';
    case 'PRO':
      return 'Pro';
    case 'Free':
      return 'Free';
    default:
      return 'Business';
  }
};

export const BusinessSelect = ({
  id,
  name,
  role,
  color,
  integrations = [],
  photo,
}: BusinessSelectProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  
  // Find subscription for this business
  const businessSubscription = user?.subscriptions?.find(sub => sub.businessId === id);
  
  // Check if trial is still valid (trialEnd is a Unix timestamp)
  const currentTime = Math.floor(Date.now() / 1000);
  const isTrialActive = businessSubscription?.trialEnd && 
    businessSubscription.trialEnd > currentTime;
  
  // Get plan type for membership info
  const apiPlanType = businessSubscription?.plan?.type || 'FREE';
  const mappedPlanType = mapPlanType(apiPlanType);
  const membershipInfo = getMembershipInfo(mappedPlanType);

  // Determine which icon to show based on plan and trial status
  const getPlanIcon = () => {
    // If trial is active and plan is PROFESSIONAL, show hourglass
    if (isTrialActive && apiPlanType === 'PROFESSIONAL') {
      return <TrialPlanIcon width={34} height={34} />;
    }

    // For other cases, show the appropriate plan icon
    switch (apiPlanType) {
      case 'GROWTH':
        return <EnterpriseIcon width={18} height={18} />;
      case 'PRO':
        return <DiamondIcon width={18} height={18} />;
      case 'FREE':
        return <BriefcaseIcon width={18} height={18} />; // BASIC icon for expired trials or no trial
      default:
        return <BriefcaseIcon width={18} height={18} />;
    }
  };

  // Get tooltip content
  const getTooltipContent = () => {
    if (isTrialActive && apiPlanType === 'PROFESSIONAL') {
      const daysLeft = Math.ceil((businessSubscription!.trialEnd! - currentTime) / (24 * 60 * 60));
      return `Free trial - ${daysLeft} days left`;
    }
    
    // Show specific descriptions for each plan type
    switch (apiPlanType) {
      case 'GROWTH':
        return 'Growth plan';
      case 'PRO':
        return 'Professional plan';
      case 'FREE':
        return 'Free plan';
      default:
        return membershipInfo.description;
    }
  };

  // Check if Google and Yelp integrations are enabled
  const hasGoogleIntegration = integrations.some(integration => integration.type === IntegrationType.GOOGLE);
  const hasYelpIntegration = integrations.some(integration => integration.type === IntegrationType.YELP);

  const handleBusinessSelect = () => {
    // Normal business selection
    dispatch({ type: 'business/selectBusiness', payload: { id } });
    navigate('/');
  };

  return (
    <div
      id={id}
      className="cursor-pointer w-full bg-white rounded-lg shadow-md flex items-center justify-between p-2 gap-2"
      onClick={handleBusinessSelect}
    >
      <div>
        {photo ? (
          <img
            src={photo.url}
            alt={name}
            className="w-12 h-12 rounded-lg object-cover"
          />
        ) : (
          <div
            className="w-12 h-12 flex items-center justify-center font-bold text-2xl rounded-lg"
            style={{ color: `#${color}`, background: `#${color}14` }}
          >
            {name.charAt(0)}
          </div>
        )}
      </div>
      <div className="mr-auto">
        <h5 className="font-bold">{name}</h5>
        <p className="text-sm text-base-coolGray">{role}</p>
      </div>
      <div className="flex gap-1 ">
        {hasGoogleIntegration && (
          <div className="flex items-center">
            <GoogleIcon width={16} height={16} />
          </div>
        )}
        {hasYelpIntegration && (
          <div className="flex items-center">
            <YelpIcon width={16} height={16} />
          </div>
        )}
      </div>
      <div>
        <Tooltip content={<p>{getTooltipContent()}</p>}>
          <div
            className="w-9 h-9 flex items-center justify-center font-bold text-2xl rounded-lg"
            style={{
              background: isTrialActive && apiPlanType === 'PROFESSIONAL' 
                ? 'transparent' 
                : `linear-gradient(106.15deg, ${membershipInfo.color.start} -0.54%, ${membershipInfo.color.end} 100.37%)`,
            }}
          >
            {getPlanIcon()}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
